import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { ExternalPointFormComponent } from './components/external-point-form.component';
import { ControlContainer, FormGroup, FormGroupDirective } from '@angular/forms';
import {
  ExternalPoint,
  Provider,
  Interval,
  Mode,
  Registration,
  ExternalPointCard,
} from '../../shared/model/external-point.model';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Locale, LocaleBaseConfig, Channel, MultiLocaleFormData } from '@model';

@Component({
  selector: 'app-external-points',
  templateUrl: './external-points.component.html',
  styleUrls: ['./external-points.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalPointsComponent implements OnInit, OnDestroy, OnChanges {
  @Input({ required: true }) mode!: Mode;
  @Input() index = 0;
  @Input() pointId = '';
  @Input() pointSourceId = '';
  @Input() isRetired = false;
  @Input() userLocale;
  @Input() stableKey!: string;
  @Input() hasServerError = false;
  @Input() pointChannel = '';
  @Input({ required: true }) externalPoint!: ExternalPointCard | ExternalPoint;
  @Input({ required: true }) localeConfig!: LocaleBaseConfig;
  @Input() supportedLocales: Locale[] = [];
  @Input() dataProviders: Provider[] = [];
  @Input() channels: Channel[] = [];
  @Input() reportingIntervals: Interval[] = [];
  @Input() loading = { providers: false, channels: false, intervals: false };
  @Input() serviceDeliveryPointDisplayLabels: Record<string, string> = {};
  @Input() sdpDisplayLabelsByLocale: MultiLocaleFormData = {};
  panelOpenedState = false;
  usedChannels: Channel[] = [];
  selectedDataProvider: string = '-1';
  selectedChannelLabel: string = '-';
  parent?: Registration;
  registrationDisplayLabels: any;
  selectedSiteId = '';
  selectedRegistrationName = '';

  namePlaceholder = '';
  nameLabel = '';
  displayLabelsId = 'external_point_display_labels';
  descriptionPlaceholder = '';
  descriptionLabel = '';

  @Output() remove = new EventEmitter<number>();
  @Output() onClickEdit = new EventEmitter<number>();
  @Output() formChange = new EventEmitter<{ index: number; form: FormGroup }>();
  @Output() dataProviderChange = new EventEmitter<string>();
  @Output() channelChange = new EventEmitter<string>();
  @Output() sourceIdInput = new EventEmitter<string>();
  @Output() sourceIdPaste = new EventEmitter<string>();
  @Output() sourceIdBlur = new EventEmitter<string>();
  @Output() formValidity = new EventEmitter<boolean>();
  @ViewChild(ExternalPointFormComponent) formCmp?: ExternalPointFormComponent;

  private _subscriptions: Subscription[] = [];

  get isMode(): string {
    return this.mode;
  }

  get displayLabel() {
    if (
      this.externalPoint &&
      this.externalPoint.displayLabels &&
      this.userLocale &&
      this.externalPoint.displayLabels[this.userLocale]
    ) {
      return this.externalPoint.displayLabels[this.userLocale];
    }
    return '';
  }
  get isEditing(): boolean {
    return this.mode === 'edit' || this.mode === 'create';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get sourceIdLabel(): string {
    return this.externalPoint.alternateIds?.sourceId ?? '-';
  }

  get displayLabelForm() {
    if (this.formCmp?.externalPointsForm) {
      return this.formCmp.externalPointsForm.get('displayLabels').value[this.userLocale];
    }
  }
  constructor(private translateService: TranslateService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.panelOpenedState =
      'expanded' in this.externalPoint ? (this.externalPoint as ExternalPointCard).expanded : false;
    this.initializeTranslations();
  }

  expandPanel(): void {
    this.panelOpenedState = true;
  }

  collapsePanel(): void {
    this.panelOpenedState = false;
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['index'] && !changes['index'].firstChange && this.formCmp) {
      this.formChange.emit({ index: this.index, form: (this.formCmp as any).form as FormGroup });
    }
    if (changes['channels']) {
      this.selectedChannelLabel =
        this.channels.find((ch) => ch.id === String(this.externalPoint.channelId))?.displayLabel || '-';
    }
  }

  private initializeTranslations(): void {
    this.nameLabel = this.translateService.instant('EXTERNAL_POINTS.NAME');
    this.namePlaceholder = this.translateService.instant('EXTERNAL_POINTS.NAME_PLACEHOLDER');
    this.descriptionLabel = this.translateService.instant('EXTERNAL_POINTS.DESCRIPTION');
    this.descriptionPlaceholder = this.translateService.instant('EXTERNAL_POINTS.DESCRIPTION_PLACEHOLDER');
  }

  getSelectedChannelLabel(): string {
    return this.selectedChannelLabel;
  }

  getSubtitle(): string {
    if (this.isEditing) {
      if (this.panelOpenedState) {
        return '';
      } else {
        return this.formCmp?.externalPointsForm.get('sourceId').value;
      }
    } else if (this.isMode === 'view') {
      return this.externalPoint.alternateIds?.sourceId;
    }
  }
  getServiceDeliveryPointDisplayLabels(): string {
    if (this.isMode === 'view') {
      return this.externalPoint.displayLabel;
    }
    return this.displayLabelForm;
  }
  getDataProviderLabel(): string {
    return this.dataProviders.find((dp) => dp.id === this.externalPoint.dataproviderId)?.displayLabel || '-';
  }

  getBaselinePointReportingIntervalLabel() {
    if (this.reportingIntervals.length > 0) {
      const reportingInterval = this.reportingIntervals.find(
        (interval: any) => this.externalPoint.reportingIntervalMs == interval.durationInMilliseconds,
      );
      if (reportingInterval) {
        return reportingInterval.displayLabel;
      }

      return '';
    }
    return '-';
  }
  onRegistrationSelect(registration: Registration): void {
    this.selectedRegistrationName = registration.name;
    this.parent = registration;
    this.registrationDisplayLabels = registration.displayLabels;
    this.cdr.markForCheck();
  }

  onToggle(): void {
    this.onClickEdit.emit(this.index);
  }

  onRemove(event: Event): void {
    event.stopPropagation();
    this.remove.emit(this.index);
  }

  markAllAsTouched(): void {
    this.formCmp?.markAllAsTouched();
  }

  isValid(): boolean {
    return !!this.formCmp?.valid;
  }

  getFormData(): ExternalPoint {
    return (this.formCmp as any)?.form?.getRawValue() as ExternalPoint;
  }
  handleFormChange(event: { index: number; form: FormGroup }) {
    this.formChange.emit(event);
  }

  handleDataProviderChange(id: string) {
    this.dataProviderChange.emit(id);
  }

  handleChannelChange(id: string) {
    const selectedChannel = this.channels.find((ch) => ch.id === id);
    if (selectedChannel) {
      this.selectedChannelLabel = selectedChannel.displayLabel;
      this.cdr.markForCheck();
    }
    this.channelChange.emit(id);
  }

  handleSourceIdInput(v: string) {
    this.sourceIdInput.emit(v);
  }

  handleSourceIdPaste(v: string) {
    this.sourceIdPaste.emit(v);
  }

  handleSourceIdBlur(v: string) {
    this.sourceIdBlur.emit(v);
  }
  handleFormValidity(isValid: boolean) {
    this.formValidity.emit(isValid);
  }
}
